import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useMemo, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getLocationData } from '../../utils/housingData';
import ProgressDashes from '../../components/ProgressDashes';
import { StoryNavigation } from '../../components/StoryNavigation';
import { useStoryNavigation } from '../../hooks/useStoryNavigation';

interface LocationData {
  country: string;
  city: string;
  data: {
    ratioNow: number;
    ratio1980: number;
    currency: string;
    avgIncome: number;
    avgDeposit: number;
  };
}

const RealityCheckPage = () => {
  const navigate = useNavigate();
  const { goToNext, goToPrev, hasNext, hasPrev } = useStoryNavigation();
  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);
  const [locationError, setLocationError] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(10);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (timerStarted && timeRemaining > 0) {
      timer = setTimeout(() => {
        setTimeRemaining(prev => prev - 1);
      }, 1000);
    } else if (timeRemaining === 0) {
      navigate('/insights/top-streaks');
    }
    return () => clearTimeout(timer);
  }, [timerStarted, timeRemaining, navigate]);

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const data = await getLocationData(
              position.coords.latitude,
              position.coords.longitude
            );
            setLocationData(data);
            setShowLocationPrompt(false);
            // Start the timer only after location is set
            setTimerStarted(true);
          } catch (error) {
            setLocationError(true);
          }
        },
        () => {
          // Fallback to default location if geolocation fails
          getLocationData(37.7749, -122.4194)
            .then(data => {
              setLocationData(data);
              setShowLocationPrompt(false);
              // Start the timer with fallback location too
              setTimerStarted(true);
            })
            .catch(() => setLocationError(true));
        }
      );
    } else {
      // Fallback for browsers without geolocation
      getLocationData(37.7749, -122.4194)
        .then(data => {
          setLocationData(data);
          setShowLocationPrompt(false);
          // Start the timer with fallback location too
          setTimerStarted(true);
        })
        .catch(() => setLocationError(true));
    }
  };

  const defaultData = {
    city: "San Francisco",
    data: {
      ratioNow: 11,
      ratio1980: 1.2,
    }
  };

  return (
    <div className="min-h-screen bg-[#8364FF]/[0.07] relative">
      <StoryNavigation
        onNext={goToNext}
        onPrev={goToPrev}
        hasNext={hasNext}
        hasPrev={hasPrev}
      />
      <div className="px-4 sm:px-16 pt-16 sm:pt-20 max-w-4xl mx-auto">
        {/* Progress dashes */}
        <ProgressDashes currentStep={8} totalSteps={11} />

        <div className="px-2 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-1 sm:space-y-2"
          >
            <h1 className="text-2xl sm:text-[34px] leading-[140%] font-black text-[#221A51]">
              Reality
              <br />
              Check
            </h1>
          </motion.div>

          <div className="mt-8 sm:mt-12 relative">
            {/* Blurred Background Content */}
            <div className={`${showLocationPrompt ? 'blur-sm' : ''} transition-all duration-300`}>
              <div className="bg-[#8364FF] rounded-xl p-4 sm:p-8 border-2 border-[#8364FF] shadow-[4px_4px_0px_0px_#9B86EA] relative">
                {/* Location Badge */}
                <div className="absolute -top-3 right-4 sm:right-8 bg-white px-3 sm:px-4 py-1 rounded-md border-2 border-[#221A51] text-[#221A51] text-xs sm:text-sm font-bold shadow-[2px_2px_0px_0px_#9B86EA]">
                  {locationData?.city || defaultData.city}
                </div>
                
                <div className="bg-[#1A1425] p-4 sm:p-6 rounded-lg">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-center space-y-4 sm:space-y-6"
                  >
                    {/* Main Stats Display */}
                    <div className="text-sm sm:text-base text-white/80">
                      Average house deposit is
                    </div>
                    <div className="font-mono text-5xl sm:text-[84px] font-black text-[#FF9B9B] tracking-wider tabular-nums">
                      {locationData?.data.ratioNow || defaultData.data.ratioNow}x
                    </div>
                    <div className="text-lg sm:text-xl text-white/80">
                      your annual income
                    </div>
                  </motion.div>
                </div>

                {/* Historical Comparison */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.6 }}
                  className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white/10 rounded-lg text-center"
                >
                  <div className="text-white/80 text-xs sm:text-sm">
                    In 1980, it was just
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-[#9BFFA9]">
                    {locationData?.data.ratio1980 || defaultData.data.ratio1980}x
                  </div>
                  <div className="text-white/80 text-xs sm:text-sm">
                    annual income
                  </div>
                </motion.div>

                {/* Impact Message */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 }}
                  className="mt-4 sm:mt-6 text-center"
                >
                  <div className="text-lg sm:text-xl font-bold text-white">
                    This isn't a you problem.
                  </div>
                  <div className="text-white/80 text-xs sm:text-sm mt-2">
                    House prices have outpaced wages significantly
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Location Prompt Overlay */}
            {showLocationPrompt && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center space-y-3 sm:space-y-4">
                  <button
                    onClick={handleGetLocation}
                    className="bg-[#9B86EA] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold hover:bg-[#8A75D9] transition-colors shadow-[2px_2px_0px_0px_#221A51] text-sm sm:text-base"
                  >
                    Enable Location
                  </button>
                  <div className="text-xs sm:text-sm text-[#221A51]/60">
                    to see data for your area
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Decorative elements */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="fixed bottom-0 right-0 w-48 h-48 opacity-20 -z-10"
          >
            <img src="/stars.svg" alt="" className="w-full h-full" />
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default RealityCheckPage;
